import { useState, useEffect } from 'react';
import {
  Box, Typography, CircularProgress, Alert, Button,
  FormControl, InputLabel, Select, MenuItem, Stack,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { vortxColors, useAuth, useUnifiedProjects } from '@vortx/shared-ui';

const PHD_API_BASE = process.env.REACT_APP_PHD_API_URL || 'http://localhost:5002';

export default function ProjectPicker({ onProjectLoaded }) {
  const { currentUser } = useAuth();
  const { projects, loading: projectsLoading } = useUnifiedProjects(null);

  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [scenarios, setScenarios] = useState([]);
  const [scenariosLoading, setScenariosLoading] = useState(false);
  const [selectedScenarioId, setSelectedScenarioId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const availableProjects = (projects || []).filter(p => p.name);

  // Load scenarios when project changes
  useEffect(() => {
    if (!selectedProjectId) {
      setScenarios([]);
      setSelectedScenarioId('');
      return;
    }

    const project = availableProjects.find(p => p.id === selectedProjectId);
    const userId = project?.user_id || currentUser?.uid;
    if (!userId) return;

    setScenariosLoading(true);
    setScenarios([]);
    setSelectedScenarioId('');

    fetch(`${PHD_API_BASE}/api/calibration/list-scenarios?user_id=${userId}&project_id=${selectedProjectId}`)
      .then(res => res.json())
      .then(data => {
        const list = data.scenarios || [];
        setScenarios(list);
        if (list.length === 1) {
          setSelectedScenarioId(list[0].id);
        }
      })
      .catch(err => setError(err.message))
      .finally(() => setScenariosLoading(false));
  }, [selectedProjectId]);

  const handleLoad = async () => {
    if (!selectedProjectId || !selectedScenarioId) return;

    const project = availableProjects.find(p => p.id === selectedProjectId);
    const userId = project?.user_id || currentUser?.uid;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${PHD_API_BASE}/api/calibration/load-project`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          project_id: selectedProjectId,
          scenario_id: selectedScenarioId,
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Failed to load project (${response.status})`);
      }

      const data = await response.json();
      onProjectLoaded({
        scenario_data: data.scenario_data,
        digital_twin_summary: data.digital_twin_summary,
      });
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (projectsLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
        <CircularProgress size={20} sx={{ color: vortxColors.gold }} />
        <Typography variant="body2" sx={{ color: vortxColors.stone }}>Loading projects...</Typography>
      </Box>
    );
  }

  if (availableProjects.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: vortxColors.stone, py: 1 }}>
        No projects found.
      </Typography>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={2} alignItems="flex-end">
        {/* Project dropdown */}
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel sx={{ color: vortxColors.stone }}>Project</InputLabel>
          <Select
            label="Project"
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
            sx={{ color: vortxColors.white }}
          >
            {availableProjects.map(p => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Scenario dropdown */}
        <FormControl size="small" sx={{ minWidth: 200 }} disabled={!selectedProjectId || scenariosLoading}>
          <InputLabel sx={{ color: vortxColors.stone }}>Scenario</InputLabel>
          <Select
            label="Scenario"
            value={selectedScenarioId}
            onChange={(e) => setSelectedScenarioId(e.target.value)}
            sx={{ color: vortxColors.white }}
          >
            {scenariosLoading ? (
              <MenuItem disabled>Loading...</MenuItem>
            ) : scenarios.length === 0 ? (
              <MenuItem disabled>No scenarios</MenuItem>
            ) : (
              scenarios.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.name || s.id}</MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        {/* Load button */}
        <Button
          variant="contained"
          onClick={handleLoad}
          disabled={!selectedProjectId || !selectedScenarioId || loading}
          endIcon={loading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <ArrowForwardIcon />}
          sx={{
            bgcolor: vortxColors.gold,
            '&:hover': { bgcolor: vortxColors.goldHover },
            whiteSpace: 'nowrap',
          }}
        >
          {loading ? 'Loading...' : 'Load Project'}
        </Button>
      </Stack>
    </Box>
  );
}
